import { useQuery } from '@tanstack/react-query';
import { getAllDefaults, ElementType } from '@/services/elementSizeDefaultsService';
import { logger } from '@/lib/logger';

/**
 * Hook to fetch and cache all element size defaults for the current organization.
 * Used to provide instant access to default scales when placing items on floor plan.
 */
export function useElementSizeDefaults() {
  const query = useQuery({
    queryKey: ['element-size-defaults'],
    queryFn: async () => {
      const defaults = await getAllDefaults();
      logger.log('[useElementSizeDefaults] Loaded defaults:', defaults.length);
      return defaults;
    },
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  /**
   * Get the default scale for a specific element type and category.
   * Returns the cached value immediately (no async) or null if not found.
   */
  const getDefaultScale = (
    elementType: ElementType,
    category: string,
    subcategory?: string,
  ): number | null => {
    if (!query.data) return null;

    // Try exact match first (with subcategory)
    if (subcategory) {
      const exactMatch = query.data.find(
        d =>
          d.element_type === elementType &&
          d.category === category &&
          d.subcategory === subcategory,
      );
      if (exactMatch) return exactMatch.default_scale;
    }

    // Fallback to category-level default (subcategory is empty string in DB)
    const categoryMatch = query.data.find(
      d =>
        d.element_type === elementType &&
        d.category === category &&
        (d.subcategory === '' || d.subcategory === null),
    );

    return categoryMatch?.default_scale ?? null;
  };

  return {
    ...query,
    getDefaultScale,
  };
}
